import { CaretDown, CaretLeft, GasPump, Phone } from 'phosphor-react';
import { useEffect, useState } from 'react';
import {
    Link,
    NavLink,
    useLocation,
    useNavigate,
    useSearchParams
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import AuthFlow from '../components/AuthFlow/AuthFlow';
import { formatMoney } from '../components/SearchFilter';
import Dropdown from '../components/ui/Dropdown';
import LogoutModal from '../components/ui/LogoutModal';

import BuyCarConfirm from '../components/ui/BuyCarConfirm';
import { getDiscountedPriceIfNotSold } from '../components/ui/Card';
import LanguageDropdown from '../components/ui/LanguageDropDown';
import {
    $FIXME,
    COUNTRY_TOP_LEVEL_DOMAIN,
    IMAGE_URL,
    ROUTES,
    whatsappUrl
} from '../constants';
import i18n from '../localization';
import { ICombineReducerType } from '../redux/reducers';
import { AuthTypes } from '../redux/types/AuthTypes';
import { WISH_LIST_SUCCESS } from '../redux/types/wishListTypes';
import { addImageFallback } from '../utils/Helper/ImagePlaceholder';
import GetFeaturedList from '../utils/Hooks/GetFeaturedList';
import GetSpecification, {
    GetSpecificationWeight
} from '../utils/Hooks/GetSpecification';
import useScrollspy from '../utils/Hooks/ScrollSpy';
import { DEFAULT_LANG, getTopLevelDomain } from '../utils/translation';

interface Props {
    whiteBackground?: boolean;
    ShowdetailsNav?: boolean;
    showAccountNav?: boolean;
    showInterestNav?: boolean;
    headerText?: string;
    setActiveNavClick?: $FIXME;
    newCarFlag?: boolean;
}

const Navbar = (props: Props) => {
    const { newCarFlag = false } = props;
    const { showLoginModal, isloggedIn, user } = useSelector(
        (state: ICombineReducerType) => state.auth
    );

    const { phoneNumber } = useSelector(
        (state: ICombineReducerType) => state.contact.contact
    );
    const { car } = useSelector(
        (state: ICombineReducerType) => state.carListing
    );
    const { appendClassToNav } = useSelector(
        (state: ICombineReducerType) => state.appendClassInNavbarReducer
    );

    const [navbar, setNavbar] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(showLoginModal);
    const [showLogout, setShowLogout] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const lang = i18n.language;
    const [showBuyCarModal, setShowBuyCarModal] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const topLevelDomain = getTopLevelDomain();

    const isCarAmigo = topLevelDomain === COUNTRY_TOP_LEVEL_DOMAIN.sn;

    const [NavbarArray, setNavbarArray] = useState([
        {
            title: 'sell',
            to: '/sell-car'
        },
        {
            title: 'finance',
            to: '/car-finance'
        },
        {
            title: 'insure',
            to: '/car-insurance'
        },
        {
            title: 'blog',
            to: '/blog'
        },
        {
            title: 'tools',
            to: null,
            subItems: [
                {
                    title: 'electricityPriceTracker',
                    to: '/electricity-price-tracker'
                },
                { title: 'fuelPriceTracker', to: '/fuel-price-tracker' },
                { title: 'currencyConverter', to: '/currency-converter' },
                { title: 'miToKmConverter', to: '/distance-converter' },
                {
                    title: 'drivingCostCalculator',
                    to: '/driving-cost-calculator'
                },
                { title: 'emissionEstimator', to: '/co2-emission-estimator' },
                { title: 'topCarSales', to: '/best-selling-cars-of-the-year' },
                { title: 'carLoanCalculator', to: '/car-loan-calculator' }
            ]
        }
    ]);

    useEffect(() => {
        if (isCarAmigo) {
            setNavbarArray((prevArray) => [
                {
                    title: 'buy',
                    to: null,
                    subItems: [
                        {
                            title: 'buyAllCars',
                            to: '/buy-cars'
                        },
                        {
                            title: 'buyNewCars',
                            to: '/buy-cars?isNewCar=true'
                        },
                        {
                            title: 'buyComingCars',
                            to: '/buy-cars?incoming=true'
                        }
                    ]
                },
                ...prevArray
            ]);
        } else {
            setNavbarArray((prevArray) => [
                {
                    title: 'buy',
                    to: '/buy-cars'
                },
                ...prevArray
            ]);
        }
    }, [isCarAmigo]);

    useEffect(() => {
        window.addEventListener('scroll', changebackground);
        return () => window.removeEventListener('scroll', changebackground);
    }, []);

    const handleLogoutClick = () => {
        setShowLogout(!showLogout);
    };
    const logout = () => {
        setLoading(true);
        dispatch({ type: AuthTypes.LOG_OUT });
        dispatch({
            type: WISH_LIST_SUCCESS,
            payload: []
        });
        setShowLogout(false);
        setLoading(false);
    };

    const changebackground = () => {
        if (window.scrollY >= 60) {
            setNavbar(true);
        } else {
            setNavbar(false);
        }
    };
    const isMobileorTab = useMediaQuery({ query: '(max-width: 900px)' });

    const dispatch = useDispatch();
    const changeModal = () => {
        dispatch({ type: AuthTypes.RESET_LOGIN_MODAL });
        setShowAuthModal(!showAuthModal);
    };

    const { t } = useTranslation();
    const toolsNav = {
        key: '7',
        label: t('tools'),
        children: [
            {
                key: 'fuelPriceTracker',
                label: (
                    <NavLink
                        to={`${t(ROUTES.fuelPriceTracker, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('fuelPriceTracker')}
                    </NavLink>
                )
            },
            {
                key: 'electricityPriceTracker',
                label: (
                    <NavLink
                        to={`${t(ROUTES.electricityPriceTracker, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('electricityPriceTracker')}
                    </NavLink>
                )
            },
            {
                key: 'miToKmConverter',
                label: (
                    <NavLink
                        to={`${t(ROUTES.distanceConverter, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('miToKmConverter')}
                    </NavLink>
                )
            },
            {
                key: 'currencyConverter',
                label: (
                    <NavLink
                        to={`${t(ROUTES.currencyConverter, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('currencyConverter')}
                    </NavLink>
                )
            },
            {
                key: 'drivingCostCalculator',
                label: (
                    <NavLink
                        to={`${t(ROUTES.drivingCostCalculator, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('drivingCostCalculator')}
                    </NavLink>
                )
            },
            {
                key: 'emissionEstimator',
                label: (
                    <NavLink
                        to={`${t(ROUTES.emissionEstimator, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('emissionEstimator')}
                    </NavLink>
                )
            },
            {
                key: 'topCarSales',
                label: (
                    <NavLink
                        to={`${t(ROUTES.topCarSales, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('topCarSales')}
                    </NavLink>
                )
            },
            {
                key: 'carLoanCalculator',
                label: (
                    <NavLink
                        to={`${t(ROUTES.carLoanCalculator, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('carLoanCalculator')}
                    </NavLink>
                )
            }
        ],
        triggerSubMenuAction: 'click'
    };

    const buyNav = {
        key: '30',
        label: (
            <Link to={`${t('/buy-cars', { ns: 'routes' })}`}>{t('buy')}</Link>
        )
    };

    const buyNavWithIncomingCars = {
        key: '30',
        label: t('buy'),
        children: [
            {
                key: 'buyAllCars',
                label: (
                    <NavLink
                        to={`${t(ROUTES.buyCars, {
                            ns: 'routes'
                        })}`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('buyAllCars')}
                    </NavLink>
                )
            },
            {
                key: 'buyNewCars',
                label: (
                    <NavLink
                        to={`${t(ROUTES.buyCars, {
                            ns: 'routes'
                        })}?isNewCar=true`}
                        className={({ isActive }) =>
                            isActive
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('buyNewCars')}
                    </NavLink>
                )
            },
            {
                key: 'buyComingCars',
                label: (
                    <NavLink
                        to={`${t(ROUTES.buyCars, {
                            ns: 'routes'
                        })}?incoming=true`}
                        className={({ isActive }) =>
                            isActive && searchParams.get('coming')
                                ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                : 'mbl-dropdown-nav'
                        }
                    >
                        {t('buyComingCars')}
                    </NavLink>
                )
            }
        ]
    };

    const loggedinItems = [
        isMobileorTab ? (isCarAmigo ? buyNavWithIncomingCars : buyNav) : '',
        {
            key: '1',
            label: (
                <Link
                    to={`${t('/account', { ns: 'routes' })}${t(
                        '/edit-profile',
                        { ns: 'routes' }
                    )}`}
                >
                    {t('editProfile')}
                </Link>
            )
        },
        {
            key: '2',
            label: (
                <Link
                    to={`${t('/account', { ns: 'routes' })}${t(
                        '/sell-your-car',
                        {
                            ns: 'routes'
                        }
                    )}`}
                >
                    {t('sellYourCar')}
                </Link>
            )
        },
        {
            key: '3',
            label: (
                <Link
                    to={`${t('/account', { ns: 'routes' })}${t('/financing', {
                        ns: 'routes'
                    })}`}
                >
                    {t('financing')}
                </Link>
            )
        },
        {
            key: '4',
            label: (
                <Link
                    to={`${t('/account', { ns: 'routes' })}${t('/favourites', {
                        ns: 'routes'
                    })}`}
                >
                    {t('favourites')}
                </Link>
            )
        },
        isMobileorTab && toolsNav,
        {
            key: '5',
            label: (
                <Link
                    to={`${t('/account', { ns: 'routes' })}${t(
                        '/reset-password',
                        {
                            ns: 'routes'
                        }
                    )}`}
                >
                    {t('resetPassword')}
                </Link>
            )
        },
        {
            key: '6',
            label: (
                <button onClick={handleLogoutClick} className="auth-button">
                    {t('logout')}
                </button>
            )
        }
    ];

    const loggedinItemsMobile = [
        isMobileorTab && isCarAmigo ? buyNavWithIncomingCars : buyNav,
        isMobileorTab && {
            key: '31',
            label: (
                <Link to={`${t('/sell-car', { ns: 'routes' })}`}>
                    {t('sell')}
                </Link>
            )
        },
        {
            key: '1',
            label: (
                <Link to={`${t('/car-finance', { ns: 'routes' })}`}>
                    {t('finance')}
                </Link>
            )
        },
        {
            key: '2',
            label: (
                <Link to={`${t('/car-insurance', { ns: 'routes' })}`}>
                    {t('insure')}
                </Link>
            )
        },
        {
            key: '3',
            label: (
                <Link to={`${t('/about', { ns: 'routes' })}`}>
                    {t('about')}
                </Link>
            )
        },
        {
            key: '4',
            label: (
                <Link to={`${t('/blog', { ns: 'routes' })}`}>{t('blog')}</Link>
            )
        },
        {
            key: '5',
            label: (
                <Link to={`${t('/account', { ns: 'routes' })}`}>
                    {t('account')}
                </Link>
            )
        },
        {
            key: '7',
            label: t('tools'),
            children: [
                {
                    key: 'fuelPriceTracker',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.fuelPriceTracker, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('fuelPriceTracker')}
                        </NavLink>
                    )
                },
                {
                    key: 'electricityPriceTracker',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.electricityPriceTracker, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('electricityPriceTracker')}
                        </NavLink>
                    )
                },
                {
                    key: 'miToKmConverter',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.distanceConverter, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('miToKmConverter')}
                        </NavLink>
                    )
                },
                {
                    key: 'currencyConverter',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.currencyConverter, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('currencyConverter')}
                        </NavLink>
                    )
                },
                {
                    key: 'drivingCostCalculator',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.drivingCostCalculator, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('drivingCostCalculator')}
                        </NavLink>
                    )
                },
                {
                    key: 'emissionEstimator',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.emissionEstimator, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('emissionEstimator')}
                        </NavLink>
                    )
                },
                {
                    key: 'topCarSales',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.topCarSales, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('topCarSales')}
                        </NavLink>
                    )
                },
                {
                    key: 'carLoanCalculator',
                    label: (
                        <NavLink
                            to={`${t(ROUTES.carLoanCalculator, {
                                ns: 'routes'
                            })}`}
                            className={({ isActive }) =>
                                isActive
                                    ? 'mbl-dropdown-nav mbl-dropdown-nav-active'
                                    : 'mbl-dropdown-nav'
                            }
                        >
                            {t('carLoanCalculator')}
                        </NavLink>
                    )
                }
            ],
            triggerSubMenuAction: 'click'
        },
        {
            key: '6',
            label: (
                <button onClick={handleLogoutClick} className="auth-button">
                    {t('logout')}
                </button>
            )
        }
    ];

    const loggedoutItemsMobile = [
        isMobileorTab && isCarAmigo ? buyNavWithIncomingCars : buyNav,
        buyNavWithIncomingCars,
        isMobileorTab && {
            key: '31',
            label: (
                <Link to={`${t('/sell-car', { ns: 'routes' })}`}>
                    {t('sell')}
                </Link>
            )
        },
        {
            key: '1',
            label: (
                <Link to={`${t('/car-finance', { ns: 'routes' })}`}>
                    {t('finance')}
                </Link>
            )
        },
        {
            key: '2',
            label: (
                <Link to={`${t('/car-insurance', { ns: 'routes' })}`}>
                    {t('insure')}
                </Link>
            )
        },

        {
            key: '3',
            label: (
                <Link to={`${t('/blog', { ns: 'routes' })}`}>{t('blog')}</Link>
            )
        },
        isMobileorTab && toolsNav,
        {
            key: '8',
            label: (
                <button onClick={changeModal} className="auth-button">
                    {t('signIn')}
                </button>
            )
        }
    ];

    const loggedoutItems = [
        {
            key: '1',
            label: (
                <button onClick={changeModal} className="auth-button">
                    {t('signIn')}
                </button>
            )
        }
    ];

    const sidebarProps = { items: isloggedIn ? loggedinItems : loggedoutItems };
    const sidebarPropsMobile = {
        items: isloggedIn ? loggedinItemsMobile : loggedoutItemsMobile
    };

    const goToAccount = () => {
        navigate(t('/account', { ns: 'routes' }));
    };
    const goBack = () => {
        navigate(-1);
    };
    const goToInterested = (id: string | undefined) => {
        navigate(`/interested/${id}`);
    };
    let image = '';
    if (car.images) image = JSON.parse(car?.images)[0];

    if (props.ShowdetailsNav && navbar) {
        return (
            <DetailsNav
                image={image.replace(/(vehicles\/\d{4}\/\d{2}\/)/, '$1small/')}
                car={car}
                onclick={() => {
                    if (newCarFlag) setShowBuyCarModal(true);
                    else goToInterested(car?._id);
                }}
                t={t}
                sidebarProps={sidebarPropsMobile}
                setActiveNav={props.setActiveNavClick}
                newCarFlag={newCarFlag}
                showBuyCarModal={showBuyCarModal}
                setShowBuyCarModal={setShowBuyCarModal}
            />
        );
    } else {
        return (
            <>
                <div
                    className={
                        navbar &&
                        props.whiteBackground &&
                        location.pathname.slice(1, 5) === t('/car')
                            ? `navbar active detail-nav ${
                                  showAuthModal || appendClassToNav
                                      ? 'authModal'
                                      : ''
                              }`
                            : navbar || props.whiteBackground
                            ? `navbar active  hide-logo ${
                                  showAuthModal || appendClassToNav
                                      ? 'authModal'
                                      : ''
                              }`
                            : `navbar padding ${
                                  showAuthModal || appendClassToNav
                                      ? 'authModal'
                                      : ''
                              }`
                    }
                >
                    <div
                        className={
                            navbar &&
                            props.whiteBackground &&
                            location.pathname.slice(1, 5) === t('/car')
                                ? ''
                                : navbar || props.whiteBackground
                                ? 'padding padding-container'
                                : 'padding'
                        }
                    >
                        <Link
                            to={lang === DEFAULT_LANG ? '/' : `/${lang}`}
                            className={
                                props.showAccountNav || props.showInterestNav
                                    ? 'hide'
                                    : 'show'
                            }
                        >
                            <div className="logo-container">
                                <img
                                    src={
                                        navbar || props.whiteBackground
                                            ? '/logos/logo-text-blue.svg'
                                            : '/logos/logo-text-white.svg'
                                    }
                                    alt="auto24"
                                    loading="lazy"
                                />
                            </div>
                        </Link>

                        <div
                            className={
                                props.whiteBackground &&
                                (props.showAccountNav || props.showInterestNav)
                                    ? 'navbar-right accounting-nav'
                                    : 'navbar-right'
                            }
                        >
                            <div className="navbar-right_linksMobile">
                                {props.showAccountNav ||
                                props.showInterestNav ? (
                                    <>
                                        <LanguageDropdown
                                            class={
                                                navbar || props.whiteBackground
                                                    ? 'active noborder'
                                                    : 'noborder'
                                            }
                                            mobile={true}
                                        />
                                        <AccountNav
                                            onclick={
                                                props.showAccountNav
                                                    ? goToAccount
                                                    : goBack
                                            }
                                            sidebarProps={sidebarProps}
                                            text={
                                                props.showInterestNav
                                                    ? t('backToCarDetail')
                                                    : props.headerText
                                            }
                                        />
                                    </>
                                ) : (
                                    <>
                                        {!isMobileorTab && (
                                            <ul className="navbar-right-links">
                                                {NavbarArray.map(
                                                    (item, index) => {
                                                        return (
                                                            <li
                                                                key={index}
                                                                className="navbar-item"
                                                                onMouseEnter={() =>
                                                                    setDropdownOpen(
                                                                        item?.title ??
                                                                            null
                                                                    )
                                                                }
                                                                onMouseLeave={() =>
                                                                    setDropdownOpen(
                                                                        null
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    dropdownOpen ===
                                                                    item?.title
                                                                        ? setDropdownOpen(
                                                                              null
                                                                          )
                                                                        : setDropdownOpen(
                                                                              item?.title
                                                                          )
                                                                }
                                                            >
                                                                {item.subItems ? (
                                                                    <>
                                                                        <span className="navbar-right-link items-center">
                                                                            {t(
                                                                                item.title
                                                                            )}{' '}
                                                                            <CaretDown
                                                                                style={{
                                                                                    marginLeft: 4
                                                                                }}
                                                                            />
                                                                        </span>
                                                                        <ul
                                                                            className={`dropdown-menu ${
                                                                                dropdownOpen ===
                                                                                item.title
                                                                                    ? 'show'
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            {item.subItems
                                                                                .sort(
                                                                                    (
                                                                                        a,
                                                                                        b
                                                                                    ) => {
                                                                                        return t(
                                                                                            a.title
                                                                                        ).localeCompare(
                                                                                            t(
                                                                                                b.title
                                                                                            )
                                                                                        );
                                                                                    }
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        subItem,
                                                                                        subIndex
                                                                                    ) => (
                                                                                        <li
                                                                                            key={
                                                                                                subIndex
                                                                                            }
                                                                                        >
                                                                                            <NavLink
                                                                                                to={t(
                                                                                                    subItem.to,
                                                                                                    {
                                                                                                        ns: 'routes'
                                                                                                    }
                                                                                                )}
                                                                                                className={({
                                                                                                    isActive
                                                                                                }) =>
                                                                                                    `dropdown-item ${
                                                                                                        isActive
                                                                                                            ? 'dropdown-item-active'
                                                                                                            : ''
                                                                                                    }`
                                                                                                }
                                                                                            >
                                                                                                {t(
                                                                                                    subItem.title
                                                                                                )}
                                                                                            </NavLink>
                                                                                        </li>
                                                                                    )
                                                                                )}
                                                                        </ul>
                                                                    </>
                                                                ) : (
                                                                    <NavLink
                                                                        to={t(
                                                                            item.to,
                                                                            {
                                                                                ns: 'routes'
                                                                            }
                                                                        )}
                                                                        className={({
                                                                            isActive
                                                                        }) =>
                                                                            isActive
                                                                                ? 'navbar-right-link-active'
                                                                                : 'navbar-right-link'
                                                                        }
                                                                    >
                                                                        {t(
                                                                            item.title
                                                                        )}
                                                                    </NavLink>
                                                                )}
                                                            </li>
                                                        );
                                                    }
                                                )}
                                            </ul>
                                        )}
                                        <LanguageDropdown
                                            class={
                                                navbar || props.whiteBackground
                                                    ? 'active'
                                                    : ''
                                            }
                                            mobile={true}
                                        />
                                        <div>
                                            <Dropdown
                                                {...sidebarPropsMobile}
                                                isloggedIn={isloggedIn}
                                                userName={user?.name}
                                                changeModal={changeModal}
                                            ></Dropdown>
                                        </div>{' '}
                                    </>
                                )}
                            </div>

                            <div className="navbar-right__desktop">
                                <div className="navbar-right__links">
                                    <ul className="navbar-right-links">
                                        {NavbarArray.map((item, index) => {
                                            return (
                                                <li
                                                    key={index}
                                                    className="navbar-item"
                                                    onMouseEnter={() =>
                                                        setDropdownOpen(
                                                            item?.title ?? null
                                                        )
                                                    }
                                                    onMouseLeave={() =>
                                                        setDropdownOpen(null)
                                                    }
                                                    onClick={() =>
                                                        dropdownOpen ===
                                                        item?.title
                                                            ? setDropdownOpen(
                                                                  null
                                                              )
                                                            : setDropdownOpen(
                                                                  item?.title
                                                              )
                                                    }
                                                >
                                                    {item.subItems ? (
                                                        <>
                                                            <span className="navbar-right-link items-center">
                                                                {t(item.title)}{' '}
                                                                <CaretDown
                                                                    style={{
                                                                        marginLeft: 4
                                                                    }}
                                                                />
                                                            </span>
                                                            <ul
                                                                className={`dropdown-menu ${
                                                                    dropdownOpen ===
                                                                    item.title
                                                                        ? 'show'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {item.subItems
                                                                    .sort(
                                                                        (
                                                                            a,
                                                                            b
                                                                        ) => {
                                                                            return t(
                                                                                a.title
                                                                            ).localeCompare(
                                                                                t(
                                                                                    b.title
                                                                                )
                                                                            );
                                                                        }
                                                                    )
                                                                    .map(
                                                                        (
                                                                            subItem,
                                                                            subIndex
                                                                        ) => (
                                                                            <li
                                                                                key={
                                                                                    subIndex
                                                                                }
                                                                            >
                                                                                <NavLink
                                                                                    to={t(
                                                                                        subItem.to,
                                                                                        {
                                                                                            ns: 'routes'
                                                                                        }
                                                                                    )}
                                                                                    className={({
                                                                                        isActive
                                                                                    }) =>
                                                                                        `dropdown-item ${
                                                                                            isActive
                                                                                                ? 'dropdown-item-active'
                                                                                                : ''
                                                                                        }`
                                                                                    }
                                                                                >
                                                                                    {t(
                                                                                        subItem.title
                                                                                    )}
                                                                                </NavLink>
                                                                            </li>
                                                                        )
                                                                    )}
                                                            </ul>
                                                        </>
                                                    ) : (
                                                        <NavLink
                                                            to={t(item.to, {
                                                                ns: 'routes'
                                                            })}
                                                            className={({
                                                                isActive
                                                            }) =>
                                                                isActive
                                                                    ? 'navbar-right-link-active'
                                                                    : 'navbar-right-link'
                                                            }
                                                        >
                                                            {t(item.title)}
                                                        </NavLink>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>

                                {!!phoneNumber && (
                                    <a
                                        className="navbar-contact"
                                        href={`tel:${phoneNumber.code}${phoneNumber.number}`}
                                        rel="noreferrer"
                                    >
                                        <div className="whatsapp-logo-container">
                                            <Phone size={20} />
                                        </div>
                                        <div className="contact-number">
                                            <span className="contact-number-call">
                                                {t('callUsAt')}
                                            </span>
                                            <span className="contact-number-number">
                                                {phoneNumber.code}{' '}
                                                {phoneNumber.number}
                                            </span>
                                        </div>
                                    </a>
                                )}
                                <LanguageDropdown
                                    class={
                                        navbar || props.whiteBackground
                                            ? 'active'
                                            : ''
                                    }
                                />
                                <div>
                                    <Dropdown
                                        {...sidebarProps}
                                        isloggedIn={isloggedIn}
                                        userName={user?.name}
                                        changeModal={changeModal}
                                    ></Dropdown>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {showAuthModal ? (
                    <AuthFlow
                        isVisible={showAuthModal}
                        handleCancel={changeModal}
                    />
                ) : null}
                <LogoutModal
                    visible={showLogout}
                    okHandler={logout}
                    cancelHandler={handleLogoutClick}
                    loading={loading}
                />
            </>
        );
    }
};

export default Navbar;

const DetailsNav = (props: $FIXME) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const topLevelDomain = getTopLevelDomain();
    const { phoneNumber, whatsapp } = useSelector(
        (state: ICombineReducerType) => state.contact.contact
    );

    const pageUrl = window.location.href;

    const message = `${t('whatsappUrlText')}  ${props?.car?.brand?.brand} ${
        props?.car?.model?.model
    } ${props?.car.modelYear} ${props?.car?.trim || ''} `;
    const whatsappShareUrl = `${whatsappUrl}${phoneNumber?.code?.replace(
        '+',
        ''
    )}${whatsapp}/?text=${message} on ${pageUrl}`;

    const goBack = () => {
        navigate(-1);
    };

    return useMediaQuery({ query: '(max-width: 900px)' }) ? (
        <div className="details-account-nav">
            <AccountNav
                classname="padding"
                gotoCarListing="backToTheCarListing"
                onclick={goBack}
                sidebarProps={props.sidebarProps}
            />
            <StickyNav setActiveNav={props.setActiveNav} />
        </div>
    ) : (
        <>
            <BuyCarConfirm
                open={props.showBuyCarModal}
                setModalOpen={props.setShowBuyCarModal}
                carId={props?.car?._id ?? '0'}
            />
            <div className="detailsnav">
                <div className="padding details-wrapper">
                    <div className="car-card__left">
                        <div className="car-image">
                            <img
                                src={IMAGE_URL + props.image}
                                alt="carImage"
                                onError={addImageFallback}
                                loading="lazy"
                            />
                        </div>
                        <div className="car-deets">
                            <h5 className="car-name">
                                {props?.car?.brand?.brand}{' '}
                                {props.car?.model?.model}{' '}
                                {props?.car?.modelYear} {props?.car?.trim || ''}
                            </h5>
                            <div className="car-features">
                                <div className="features-container">
                                    <div className="features-icons">
                                        <img
                                            src="/logos/automatic.svg"
                                            alt="automatic"
                                            loading="lazy"
                                        />
                                    </div>
                                    {props?.car?.transmission &&
                                        t(props.car.transmission)}
                                </div>
                                <div className="features-container">
                                    <GasPump size={17} color="#454545" />
                                    {props?.car?.fuelType &&
                                        t(props?.car.fuelType)}
                                </div>

                                <div className="features-container">
                                    <div className="features-icons">
                                        <img
                                            src="/logos/timeline.svg"
                                            alt="timeline"
                                            loading="lazy"
                                        />
                                    </div>
                                    <span>
                                        {' '}
                                        {formatMoney(props?.car?.mileage) ??
                                            'N/A'}{' '}
                                        km
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="car-card__price">
                            <span>
                                {topLevelDomain ===
                                COUNTRY_TOP_LEVEL_DOMAIN.coza
                                    ? process.env.REACT_APP_DEFAULT_CURRENCY
                                    : ''}{' '}
                                {props.car?.status === 'sold' &&
                                    formatMoney(props.car?.price)}
                                {props.car?.status !== 'sold' &&
                                    (String(props?.car?.discount) !== '0' &&
                                    String(props?.car?.discount) !== 'null' &&
                                    props.car?.price &&
                                    String(props.car?.price) !== '0'
                                        ? getDiscountedPriceIfNotSold(
                                              props.car?.price,
                                              props.car.discount
                                          )
                                        : formatMoney(props.car?.price))}{' '}
                                {topLevelDomain !==
                                COUNTRY_TOP_LEVEL_DOMAIN.coza
                                    ? process.env.REACT_APP_DEFAULT_CURRENCY
                                    : ''}
                            </span>
                            {props.car?.status !== 'sold' &&
                                String(props.car?.discount) !== '0' &&
                                String(props.car?.discount) !== 'null' && (
                                    <span>
                                        {topLevelDomain ===
                                        COUNTRY_TOP_LEVEL_DOMAIN.coza
                                            ? process.env
                                                  .REACT_APP_DEFAULT_CURRENCY
                                            : ''}{' '}
                                        {props.car.price &&
                                            props.car.price
                                                .toLocaleString()
                                                .replaceAll(',', ' ')}{' '}
                                        {topLevelDomain !==
                                        COUNTRY_TOP_LEVEL_DOMAIN.coza
                                            ? process.env
                                                  .REACT_APP_DEFAULT_CURRENCY
                                            : ''}
                                    </span>
                                )}
                        </div>
                    </div>
                    <div className="button-container">
                        <button
                            className={`bottom-btn text-uppercase ${
                                props?.car?.status.toLowerCase() === 'sold' &&
                                'button-disabled'
                            }`}
                            onClick={props.onclick}
                            disabled={
                                props?.car?.status.toLowerCase() === 'sold'
                            }
                        >
                            {props.newCarFlag
                                ? props.t('buyOnline')
                                : props.t('imInterested')}
                        </button>

                        <a
                            className=" whatsapp  car-btn"
                            target="_blank"
                            href={whatsappShareUrl}
                            rel="noreferrer"
                        >
                            <div className="whatsapp-logo-container">
                                <img
                                    src="/images/auto24-whatsapp.svg"
                                    alt="whatsapp-logo"
                                    loading="lazy"
                                />
                            </div>
                            WHATSAPP
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};
const AccountNav = (props: $FIXME) => {
    const { t } = useTranslation();

    return (
        <div className={`account-nav ${props.classname}`}>
            {/* <div className="account-nav-left">
                    <CaretLeft
                        size={20}
                        color="#454545"
                        onClick={props.onclick}
                    />

                    <p className="text">
                        {props.gotoCarListing
                            ? t(props.gotoCarListing)
                            : props.text
                            ? t(props.text)
                            : props.showAccountNav
                            ? t('Account') || 'Account'
                            : t('Back To Account') || 'Back To Account'}
                    </p>
                </div> */}
            <div className="account-nav-left">
                {props.gotoCarListing && (
                    <>
                        <CaretLeft
                            size={20}
                            color="#454545"
                            onClick={props.onclick}
                        />

                        <p className="text">
                            {props.gotoCarListing && t(props.gotoCarListing)}
                        </p>
                    </>
                )}
            </div>
            <Dropdown {...props.sidebarProps}></Dropdown>
        </div>
    );
};
const StickyNav = (props: $FIXME) => {
    const { car } = useSelector(
        (state: ICombineReducerType) => state.carListing
    );
    const { serviceHistory } = useSelector(
        (state: ICombineReducerType) => state.serviceHistory
    );
    const { t } = useTranslation();

    const StickyNavArray = [
        'overview',
        'summary',
        'specification',
        'features',
        'history'
    ];
    const activeId = useScrollspy(StickyNavArray, 120);
    const filteredFeatureList = GetFeaturedList(car);

    const filteredSpecification = GetSpecification(car);
    const filteredDataWeight = GetSpecificationWeight(car);

    return (
        <div className="sticky-nav padding">
            <ul>
                <li
                    key={1}
                    onClick={() => props.setActiveNav('carOverView')}
                    className={activeId === 'overview' ? 'active' : ''}
                >
                    {t('carOverview')}
                </li>
                <li
                    key={2}
                    onClick={() => props.setActiveNav('summary')}
                    className={activeId === 'summary' ? 'active' : ''}
                >
                    {t('Summary')}
                </li>
                {filteredSpecification.length === 0 &&
                filteredDataWeight.length === 0 ? null : (
                    <li
                        key={3}
                        onClick={() => props.setActiveNav('specifications')}
                        className={activeId === 'specification' ? 'active' : ''}
                    >
                        {t('specifications')}
                    </li>
                )}
                {filteredFeatureList.length > 0 && (
                    <li
                        key={4}
                        onClick={() => props.setActiveNav('features')}
                        className={activeId === 'features' ? 'active' : ''}
                    >
                        {t('features')}
                    </li>
                )}
                {serviceHistory.length !== 0 && (
                    <li
                        key={5}
                        onClick={() => props.setActiveNav('serviceHistorys')}
                        className={activeId === 'history' ? 'active' : ''}
                    >
                        {t('serviceHistory')}
                    </li>
                )}
            </ul>
        </div>
    );
};
